import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chinaOrdersApi, productsApi } from '../api/client';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';
import ConfirmDialog from '../components/ConfirmDialog';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const ChinaOrders = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState(searchParams.get('mode') || 'list'); // 'list' or 'create'
  const [productId, setProductId] = useState(searchParams.get('product_id') || null);
  const [product, setProduct] = useState(null);
  const [itemCodeInput, setItemCodeInput] = useState('');
  const [productsList, setProductsList] = useState(null);
  // orderItems - массив товаров в заказе, каждый товар может иметь несколько позиций с разными диоптриями
  const [orderItems, setOrderItems] = useState([]); // [{ product_id, product_name, item_code, positions: [{ dioptry, qty }] }]
  const [supplierName, setSupplierName] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ show: false, orderId: null });
  const [searchQuery, setSearchQuery] = useState('');
  const addedProductsRef = useRef(new Set());

  const handleSelectProduct = useCallback((selectedProduct) => {
    setProductsList(null);
    // Проверяем, есть ли уже этот товар в заказе
    setOrderItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(item => item.product_id === selectedProduct.id);
      if (existingItemIndex >= 0) {
        // Если товар уже есть, добавляем к нему новую позицию
        const newOrderItems = [...prevItems];
        newOrderItems[existingItemIndex].positions.push({ dioptry: '', qty: '' });
        return newOrderItems;
      } else {
        // Если товара нет, добавляем новый товар с одной позицией
        return [...prevItems, {
          product_id: selectedProduct.id,
          product_name: selectedProduct.name || 'Без названия',
          item_code: selectedProduct.item_code || null,
          positions: [{ dioptry: '', qty: '' }]
        }];
      }
    });
    setItemCodeInput('');
  }, []);

  // Если перешли со страницы товара, автоматически переключаемся в режим создания
  useEffect(() => {
    const productIdParam = searchParams.get('product_id');
    if (productIdParam && mode === 'list') {
      setMode('create');
    }
  }, [searchParams, mode]);

  // Добавляем товар в заказ, если перешли со страницы товара
  useEffect(() => {
    const productIdParam = searchParams.get('product_id');
    if (!productIdParam || mode !== 'create') {
      addedProductsRef.current.clear();
      return;
    }
    
    const productIdNum = parseInt(productIdParam);
    // Проверяем, не добавлен ли уже этот товар
    if (addedProductsRef.current.has(productIdNum)) {
      return;
    }
    
    addedProductsRef.current.add(productIdNum);
    // Загружаем товар и добавляем его
    productsApi.getById(productIdParam).then(response => {
      if (response.data) {
        handleSelectProduct(response.data);
      }
    }).catch(console.error);
  }, [searchParams.get('product_id'), mode, handleSelectProduct]);

  // Варианты диоптрий
  const dioptryOptions = [];
  dioptryOptions.push(0, 1.00, 1.25, 1.50, 1.75);
  for (let i = 2.00; i <= 5.00; i += 0.25) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }
  for (let i = 5.50; i <= 7.00; i += 0.50) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['china-orders'],
    queryFn: async () => {
      const response = await chinaOrdersApi.getAll();
      return response.data;
    },
    enabled: mode === 'list',
  });

  const handleSearchByItemCode = async () => {
    if (!itemCodeInput.trim()) {
      alert('Введите артикул товара');
      return;
    }

    try {
      const response = await productsApi.getByItemCode(itemCodeInput.toUpperCase());
      const data = response.data;
      
      if (!data) {
        alert('Товар не найден');
        return;
      }
      
      if (Array.isArray(data) && data.length > 1) {
        setProductsList(data);
        setProduct(null);
        setProductId(null);
      } else {
        const productToAdd = Array.isArray(data) ? data[0] : data;
        if (!productToAdd || !productToAdd.id) {
          alert('Ошибка: некорректные данные товара');
          return;
        }
        // Добавляем товар в заказ
        handleSelectProduct(productToAdd);
      }
      setItemCodeInput('');
    } catch (error) {
      console.error('Ошибка поиска товара:', error);
      if (error.response?.status === 404) {
        if (confirm('Товар не найден. Создать новый товар?')) {
          navigate(`/products/new?item_code=${itemCodeInput.toUpperCase()}`);
        }
      } else {
        alert('Ошибка поиска товара: ' + (error.message || 'Неизвестная ошибка'));
      }
    }
  };

  const addPositionToProduct = (productIndex) => {
    const newOrderItems = [...orderItems];
    newOrderItems[productIndex].positions.push({ dioptry: '', qty: '' });
    setOrderItems(newOrderItems);
  };

  const removePositionFromProduct = (productIndex, positionIndex) => {
    const newOrderItems = [...orderItems];
    if (newOrderItems[productIndex].positions.length > 1) {
      newOrderItems[productIndex].positions.splice(positionIndex, 1);
      setOrderItems(newOrderItems);
    }
  };

  const updatePosition = (productIndex, positionIndex, field, value) => {
    const newOrderItems = [...orderItems];
    newOrderItems[productIndex].positions[positionIndex][field] = value;
    setOrderItems(newOrderItems);
  };

  const removeProductFromOrder = (productIndex) => {
    const newOrderItems = orderItems.filter((_, i) => i !== productIndex);
    setOrderItems(newOrderItems);
  };

  const createMutation = useMutation({
    mutationFn: async (orderData) => {
      const response = await chinaOrdersApi.create(orderData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['china-orders']);
      alert('Заказ из Китая успешно создан!');
      setMode('list');
      setOrderItems([]);
      setProduct(null);
      setProductId(null);
      setProductsList(null);
      setSupplierName('');
      setNotes('');
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при создании заказа');
    },
  });

  const receiveMutation = useMutation({
    mutationFn: async (orderId) => {
      const response = await chinaOrdersApi.receive(orderId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['china-orders']);
      queryClient.invalidateQueries(['stock']);
      queryClient.invalidateQueries(['products']);
      setConfirmDialog({ show: false, orderId: null });
      alert('Заказ отмечен как поступивший! Товар добавлен на склад.');
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при отметке заказа');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (orderId) => {
      await chinaOrdersApi.delete(orderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['china-orders']);
      alert('Заказ удален');
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при удалении заказа');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (orderItems.length === 0) {
      alert('Добавьте хотя бы один товар в заказ');
      return;
    }

    // Собираем все валидные позиции из всех товаров
    const allItems = [];
    orderItems.forEach(orderItem => {
      orderItem.positions.forEach(position => {
        const qtyValue = parseFloat(position.qty || 0);
        if (qtyValue > 0) {
          allItems.push({
            product_id: orderItem.product_id,
            qty: qtyValue,
            dioptry: position.dioptry && position.dioptry.trim() !== '' ? parseFloat(position.dioptry) : null,
          });
        }
      });
    });

    if (allItems.length === 0) {
      alert('Добавьте хотя бы одну позицию с количеством больше нуля');
      return;
    }

    const orderData = {
      supplier_name: supplierName || null,
      notes: notes || null,
      items: allItems,
    };

    createMutation.mutate(orderData);
  };

  const handleReceive = (orderId) => {
    setConfirmDialog({ show: true, orderId });
  };

  const confirmReceive = () => {
    if (confirmDialog.orderId) {
      receiveMutation.mutate(confirmDialog.orderId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Функция для получения названия заказа (название линз)
  const getOrderTitle = (order) => {
    if (!order.items || order.items.length === 0) {
      return 'Заказ без товаров';
    }
    
    if (order.items.length === 1) {
      return order.items[0].product_name || 'Без названия';
    }
    
    // Если несколько товаров, показываем первый товар + количество остальных
    const firstItem = order.items[0];
    const remainingCount = order.items.length - 1;
    return `${firstItem.product_name || 'Без названия'}${remainingCount > 0 ? ` и еще ${remainingCount}` : ''}`;
  };

  // Функция фильтрации заказов по поисковому запросу
  const filterOrders = (orders) => {
    if (!searchQuery.trim()) {
      return orders;
    }

    const query = searchQuery.toLowerCase().trim();
    return orders.filter(order => {
      if (!order.items || order.items.length === 0) {
        return false;
      }

      return order.items.some(item => {
        const productName = (item.product_name || '').toLowerCase();
        const itemCode = (item.item_code || '').toLowerCase();
        return productName.includes(query) || itemCode.includes(query);
      });
    });
  };

  if (mode === 'create') {
    return (
      <div className="p-4 pb-24">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Заказ из Китая</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setMode('list');
              setOrderItems([]);
              setProductsList(null);
              setSupplierName('');
              setNotes('');
              addedProductsRef.current.clear();
            }}
          >
            ← Назад
          </Button>
        </div>

        {/* Поиск по артикулу */}
        {!productsList && (
          <div className="mb-4">
            <Input
              label="Артикул"
              value={itemCodeInput}
              onChange={(e) => setItemCodeInput(e.target.value.toUpperCase())}
              placeholder="Введите артикул товара"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchByItemCode();
                }
              }}
            />
            <Button
              variant="primary"
              size="lg"
              className="w-full mt-2"
              onClick={handleSearchByItemCode}
              disabled={!itemCodeInput.trim()}
            >
              🔍 Найти по артикулу
            </Button>
          </div>
        )}

        {/* Список товаров с разными диоптриями */}
        {productsList && productsList.length > 1 && (
          <Card className="mb-4">
            <h3 className="text-lg font-semibold mb-3">
              Найдено товаров с артикулом {productsList[0]?.item_code || 'N/A'}: {productsList.length}
            </h3>
            <p className="text-sm text-gray-600 mb-3">Выберите товар с нужными диоптриями:</p>
            <div className="space-y-2">
              {productsList.map((prod) => (
                <button
                  key={prod.id}
                  onClick={() => handleSelectProduct(prod)}
                  className="w-full p-3 text-left border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{prod.name || 'Без названия'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-blue-600">
                        {parseFloat(prod.price || 0).toFixed(2)} ₸
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Остаток: {formatQty(prod.stock || 0)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <Button
              variant="secondary"
              size="md"
              className="w-full mt-3"
              onClick={() => {
                setProductsList(null);
                setItemCodeInput('');
              }}
            >
              Назад
            </Button>
          </Card>
        )}

        {/* Список товаров в заказе */}
        {orderItems.length > 0 && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 mb-4">
              {orderItems.map((orderItem, productIndex) => (
                <Card key={productIndex} className="mb-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {orderItem.product_name || 'Без названия'}
                        {orderItem.item_code && (
                          <span className="text-sm font-mono text-blue-600 ml-2">
                            {orderItem.item_code}
                          </span>
                        )}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeProductFromOrder(productIndex)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium ml-2"
                    >
                      ✕ Удалить товар
                    </button>
                  </div>

                  <div className="space-y-3">
                    {orderItem.positions.map((position, positionIndex) => (
                      <div key={positionIndex} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-700 min-w-[60px]">
                            Позиция #{positionIndex + 1}
                          </span>
                          {orderItem.positions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePositionFromProduct(productIndex, positionIndex)}
                              className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              ✕ Удалить
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Диоптрии
                            </label>
                            <select
                              value={position.dioptry}
                              onChange={(e) => updatePosition(productIndex, positionIndex, 'dioptry', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                              <option value="">Не указано</option>
                              {dioptryOptions.map((value) => (
                                <option key={value} value={value}>
                                  {value === 0 ? '0' : "-" + value.toFixed(2)}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Количество
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={position.qty}
                              onChange={(e) => updatePosition(productIndex, positionIndex, 'qty', e.target.value)}
                              placeholder="0"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => addPositionToProduct(productIndex)}
                  >
                    + Добавить позицию для этого товара
                  </Button>
                </Card>
              ))}
            </div>

            {createMutation.error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {createMutation.error.response?.data?.error || 'Произошла ошибка'}
              </div>
            )}

            <Button
              type="submit"
              variant="success"
              size="lg"
              className="w-full"
              disabled={orderItems.length === 0 || createMutation.isPending}
            >
              {createMutation.isPending ? 'Создание...' : `Создать заказ (${orderItems.reduce((total, item) => total + item.positions.filter(p => parseFloat(p.qty || 0) > 0).length, 0)} позиций)`}
            </Button>
          </form>
        )}

        {/* Подсказка, если нет товаров в заказе */}
        {orderItems.length === 0 && !productsList && (
          <Card>
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg mb-2">Добавьте товары в заказ</p>
              <p className="text-sm">Найдите товар по артикулу выше</p>
            </div>
          </Card>
        )}
      </div>
    );
  }

  // Режим списка заказов
  const filteredOrders = ordersData ? filterOrders(ordersData) : [];

  return (
    <div className="p-4 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Заказы из Китая</h1>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setMode('create')}
        >
          + Новый заказ
        </Button>
      </div>

      {/* Поиск */}
      {ordersData && ordersData.length > 0 && (
        <div className="mb-4">
          <Input
            label="Поиск по артикулу или названию"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Введите артикул или название линз"
          />
        </div>
      )}

      {ordersLoading ? (
        <div className="text-center py-8 text-gray-500">Загрузка...</div>
      ) : !ordersData || ordersData.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">Нет заказов</p>
            <Button
              variant="primary"
              size="md"
              onClick={() => setMode('create')}
            >
              Создать первый заказ
            </Button>
          </div>
        </Card>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">Ничего не найдено</p>
            <p className="text-sm">Попробуйте изменить поисковый запрос</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <Card key={order.id} className={order.is_received ? 'opacity-75' : ''}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getOrderTitle(order)}
                  </h3>
                  {order.items && order.items.length > 0 && order.items[0].item_code ? (
                    <p className="text-sm font-mono text-blue-600 mt-1">
                      {order.items[0].item_code}
                    </p>
                  ) : null}
                  <p className="text-sm text-gray-600">
                    {formatDate(order.date)}
                  </p>
                  {order.supplier_name && (
                    <p className="text-sm text-gray-700 mt-1">
                      Поставщик: {order.supplier_name}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {order.is_received ? (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                      Поступило
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                      Ожидает
                    </span>
                  )}
                </div>
              </div>

              {order.items?.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Позиции ({order.items.length}):
                  </p>
                  <div className="space-y-2">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <div className="flex-1">
                          {/* <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {item.product_name || 'Без названия'}
                            </span>
                            {item.item_code && (
                              <span className="text-xs font-mono text-blue-600">{item.item_code}</span>
                            )}
                          </div> */}
                          {(() => {
                            const dioptryValue = item.dioptry;
                            if (dioptryValue === null || dioptryValue === undefined) {
                              return (
                                <span className="inline-block text-xs font-medium text-gray-600 bg-blue-50 px-2 py-1 rounded">
                                  Без диоптрий
                                  <span className="ml-1 text-blue-600">({formatQty(item.qty)})</span>
                                </span>
                              );
                            }
                            const dioptryNum = parseFloat(dioptryValue);
                            if (isNaN(dioptryNum)) {
                              return null;
                            }
                            const dioptryDisplay = dioptryNum === 0 ? '0' : "-" + dioptryNum.toFixed(2);
                            return (
                              <span className="inline-block text-xs font-medium text-gray-600 bg-blue-50 px-2 py-1 rounded">
                                {dioptryDisplay}
                                <span className="ml-1 text-blue-600">({formatQty(item.qty)})</span>
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {(order.notes || (order.is_received && order.received_date)) ? (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  {order.notes && (
                    <p className="text-xs text-gray-600 mb-2">{order.notes}</p>
                  )}
                  {order.is_received && order.received_date && (
                    <p className="text-xs text-gray-500">
                      Поступило: {formatDate(order.received_date)}
                    </p>
                  )}
                </div>
              ) : null}

              <div className={`${order.notes || (order.is_received && order.received_date) ? 'mt-3' : 'mt-3'} pt-3 border-t border-gray-200 flex gap-2`}>
                {!order.is_received && (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleReceive(order.id)}
                      disabled={receiveMutation.isPending}
                    >
                      ✓ Поступило
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm('Удалить этот заказ?')) {
                          deleteMutation.mutate(order.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      Удалить
                    </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.show}
        title="Подтверждение"
        message="Отметить заказ как поступивший? Товар будет добавлен на склад."
        onConfirm={confirmReceive}
        onCancel={() => setConfirmDialog({ show: false, orderId: null })}
        confirmText="Да, поступило"
        cancelText="Отмена"
      />
    </div>
  );
};

export default ChinaOrders;

